import { useRoute } from 'shared/library/routes/route';
import { Platform, View } from 'react-native';
import { Props, Post } from 'shared/@types';
import { useTranslation } from 'react-i18next';
import { apiRoute } from 'shared/library/api-route';
import { MarkdownView } from 'shared/components/markdown-view';
import { Image } from 'shared/components/image';
import { H1, H2 } from 'shared/components/title';
import { Text } from 'shared/components/text';
import { PostCard } from 'shared/components/post-card';
import { Link } from 'shared/components/link';
import { AdBanner } from 'shared/components/ad-banner';
import { PageBuilder } from 'shared/library/page-builder';
import { Breadcrumb } from 'shared/components/breadcrumb';
import { AuthorCard } from 'shared/components/author-card';
import { useFormatter } from 'shared/library/use-formatter';
import { getPostImage } from 'shared/library/get-model-image';
import type { Root } from 'hast';

interface ShowPostScreenProps extends Props {
  post: Post;
  relatedPosts: Post[];
  compiledPostMarkdown?: Root;
}

const ShowPostScreen = ({ post, relatedPosts, compiledPostMarkdown }: ShowPostScreenProps) => {
  const { t } = useTranslation('screens');
  const route = useRoute();
  const { formatDateCustom } = useFormatter();
  const title = getPostDisplayTitle(post);

  return (
    <>
      <View className={'px-3 pb-3 md:px-6 md:pb-12'}>
        <View className={`mx-auto w-full max-w-screen-xl`}>
          <AdBanner className={'mt-4 md:mt-6'} placement={'top'} />

          <View className={'mt-6 gap-6 md:flex-row-reverse md:items-start lg:gap-12'}>
            <View className={'md:flex-1'}>
              <Image
                src={getPostImage(post)}
                alt={post.title}
                containerClassName={'aspect-4/3 w-full'}
                imageClassName={'rounded-lg object-cover'}
                priority={true}
                sizeType={'large'}
              />
            </View>

            <View className={'md:w-1/2'}>
              {Platform.OS === 'web' && (
                <Breadcrumb
                  className={'mb-1.5'}
                  items={[
                    { text: t('blog.show_post.blog'), href: route('blog.index') },
                    {
                      text: post.tags[0]?.name ?? null,
                      href: route('blog.tags.show', { tag: post.tags[0]?.slug ?? '' }),
                    },
                  ]}
                />
              )}

              <H1 title={title} subtitle={post.summary} hideTitleWhenNativeHeader={true} />

              <Text className={'text-brand-brown-dark/70 mb-3 text-sm md:mb-6'}>
                {t('components:post_card.published')}{' '}
                {post.published_at ? formatDateCustom(post.published_at, 'do LLLL yyyy') : '-'}
                {' · '}
                {t('components:post_card.reading_time', { count: post.reading_time })}
              </Text>

              <AuthorCard author="andre" dense={true} />
            </View>
          </View>

          {Platform.OS === 'web' && (
            <AdBanner className={'mt-6 md:mt-12'} loadImmediately={true} placement={'content'} />
          )}

          <MarkdownView
            className={'mt-6 md:mt-12'}
            markdown={post.body}
            {...(Platform.OS === 'web' ? { compiledTree: compiledPostMarkdown } : {})}
            renderAds={true}
          />

          <H2 title={t('components:author_card.title')} />
          <AuthorCard author="andre" />

          <H2 title={t('blog.show_post.topics')} />
          <View className={'-m-1.5 flex-row flex-wrap'}>
            {post.tags.map((tag) => (
              <Link
                key={tag.id}
                href={route('blog.tags.show', { tag: tag.slug })}
                containerClassName={'m-1.5'}
                textClassName={
                  'block text-brand-brown-dark border-brand-brown-dark/20 rounded-full border bg-white px-3 py-2 text-sm md:text-base'
                }
              >
                {tag.name}
              </Link>
            ))}
          </View>

          <H2 title={t('blog.show_post.related_posts')} />
          <View
            className={
              Platform.OS === 'web'
                ? 'flex-cols-1 md:flex-cols-3 flex-row flex-wrap gap-6'
                : 'gap-6'
            }
          >
            {relatedPosts.map((post) => (
              <View key={post.id}>
                <PostCard post={post} />
              </View>
            ))}
          </View>
        </View>
      </View>
    </>
  );
};

ShowPostScreen.pageBuilder = new PageBuilder<ShowPostScreenProps, { post: string }>()
  .request(({ req, params }) =>
    req<{ post: Post; related_posts: Post[] }>(apiRoute('posts.show', { post: params.post })).then(
      (res) => ({ post: res.post, relatedPosts: res.related_posts }),
    ),
  )
  .deriveRouteParams((props) => ({
    post: props.post.slug,
  }));

export { ShowPostScreen };
export { getPostDisplayTitle };
export type { ShowPostScreenProps };

function getPostDisplayTitle(post: Post) {
  const normalizedTitle = normalizeKeywordText(post.title);
  const titleOverrides: Record<string, string> = {
    'köpa hund': 'Köpa hund: råd och checklista inför köpet',
    'sälja hund': 'Sälj hund: annons, köpare och trygg process',
    'köpa katt': 'Köpa katt: råd och checklista inför köpet',
    'sälja katt': 'Sälj katt: annons, köpare och trygg process',
  };

  return titleOverrides[normalizedTitle] ?? post.title;
}

function normalizeKeywordText(value: string) {
  return value.toLowerCase().normalize('NFC').replace(/\s+/g, ' ').trim();
}
